import React from 'react';
import '@atlaskit/css-reset';
import { styled } from '@compiled/react';
import { addGlobalEventProcessor } from '@sentry/browser';
import marketplace from '@atlassian/amkt-upm-spi-impl';
import { Emcee, getApplicationConfigForEnv, type ApplicationConfig } from '@atlassian/emcee';
import LoginRedirect from '@atlassian/jira-common-navigation/src/login-redirect';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/index.tsx';
import { useAnalyticsEvents, fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { useRouter, type BrowserHistory } from '@atlassian/jira-router';
import { useOrgId } from '@atlassian/jira-router-resources-navigation-org-id';
import { ScreenRedirect } from '@atlassian/jira-screen-redirect/src/ui/index.tsx';
import { HistoryAdapter } from '@atlassian/jira-spa-router-adapters/src/controllers/history/index.tsx';
import { useAccountId } from '@atlassian/jira-tenant-context-controller/src/components/account-id/index.tsx';
import { useActivationId } from '@atlassian/jira-tenant-context-controller/src/components/activation-id';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useEnvironment } from '@atlassian/jira-tenant-context-controller/src/components/environment/index.tsx';
import UFOSegment from '@atlassian/jira-ufo-segment';
import { addApdexToAll } from '@atlassian/react-ufo/interaction-metrics';
import { emceePageLoad } from './utils/performance-analytics';

const onExternalSentryEvent = (callback: unknown) =>
	addGlobalEventProcessor((event) => {
		// @ts-expect-error - TS2571 - Object is of type 'unknown'.
		callback(event);
		return event;
	});

const EMCEE_BASE_PATHNAME = '/jira/marketplace';

const onRouteTransitionEnd = () => {
	emceePageLoad.stop();
	addApdexToAll({
		key: emceePageLoad.key,
		stopTime: performance.now(),
	});
};

type EmbeddedMarketplaceAppProps = {
	history: BrowserHistory;
};
const EmbeddedMarketplaceApp = ({ history }: EmbeddedMarketplaceAppProps) => {
	const cloudId = useCloudId();
	const environment = useEnvironment();
	const { data: orgId } = useOrgId();
	const activationId = useActivationId();
	const applicationConfig: ApplicationConfig = getApplicationConfigForEnv(environment);
	return (
		<Emcee
			spi={marketplace({
				baseUrl: window.location.origin,
				product: 'jira',
				cloudId,
				environment,
				gqlGatewayUrl: `${window.location.origin}/gateway/api/graphql`,
				activationId,
				orgId,
			})}
			history={history}
			applicationConfig={applicationConfig}
			productVersion={window.BUILD_KEY}
			mountPath={EMCEE_BASE_PATHNAME}
			onRouteTransitionEnd={onRouteTransitionEnd}
			onExternalSentryEvent={onExternalSentryEvent}
		/>
	);
};

const MarketplaceShell = () => {
	const loggedInUserAccountId = useAccountId();
	const [{ location }] = useRouter();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	if (!loggedInUserAccountId) {
		return <LoginRedirect />;
	}

	// Append hash fragment to the existing pathname for the new emcee native URL
	if (location.hash && location.hash.includes('#!')) {
		const newPath = location.pathname + location.hash.split('#!')[1];
		const redirectAttributes = {
			previousPath: location.pathname + location.search + location.hash,
			newPath,
		};
		fireUIAnalytics(
			createAnalyticsEvent({
				action: 'redirected',
				actionSubject: 'emcee-route',
				actionSubjectId: 'MarketplaceShell',
			}),
			redirectAttributes,
		);
		return <ScreenRedirect to={newPath} persistSearchParams={false} />;
	}
	emceePageLoad.start();

	return (
		<UFOSegment name="embedded-marketplace">
			<MarketplaceContainer id="emcee">
				<JSErrorBoundary
					id="embeddedMarketplace"
					packageName="jiraSpaAppsMarketplace"
					teamName="marketplace"
				>
					{/* @ts-expect-error - TS2739 - Type '{ children: (history: any) => Element; }' is missing the following properties from type 'RouteContext': location, query, route, match, action */}
					<HistoryAdapter>
						{(history) => <EmbeddedMarketplaceApp history={history} />}
					</HistoryAdapter>
				</JSErrorBoundary>
			</MarketplaceContainer>
		</UFOSegment>
	);
};

export default MarketplaceShell;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const MarketplaceContainer = styled.div({
	height: '100%',
});
